import { defineStore } from 'pinia'
import type { IPrepareBarChartColors } from '~/constants/types/charts'
import { STORE } from '~/constants/strings'
import { ODemographicChartLabels } from '~/constants/types/charts'
import { getComputedColor } from '~/helpers/theme'

export enum ThemeType {
  DEFAULT = 'default',
  ACCESSIBILITY_MODE = 'accessibilityMode',
}

interface IThemeState {
  currentTheme: string
}

export const useThemeStore = defineStore(STORE.THEME, {
  state: (): IThemeState => ({
    currentTheme: ThemeType.DEFAULT,
  }),
  getters: {
    unknownChartColor: () => [
      {
        label: ODemographicChartLabels.UNK,
        color: getComputedColor('--ba-50-dark-gray'),
      },
    ],
    genderChartColors: () => [
      {
        label: ODemographicChartLabels.FEM,
        color: getComputedColor('--ba-secondary'),
      },
      {
        label: ODemographicChartLabels.MAL,
        color: getComputedColor('--ba-darkGray'),
      },
    ],
    minAndNonMinChartColors: () => [
      {
        label: ODemographicChartLabels.MIN,
        color: getComputedColor('--ba-primary'),
      },
      {
        label: ODemographicChartLabels.NON_MIN,
        color: getComputedColor('--ba-darkGray'),
      },
    ],
    minAndGenderChartColors: () => [
      {
        label: ODemographicChartLabels.MIN,
        color: getComputedColor('--ba-primary'),
      },
      {
        label: ODemographicChartLabels.FEM,
        color: getComputedColor('--ba-secondary'),
      },
    ],
    individualRacesChartColors: () => [
      {
        label: ODemographicChartLabels.ASI,
        color: getComputedColor('--ba-asi-violet'),
      },
      {
        label: ODemographicChartLabels.BLK,
        color: getComputedColor('--ba-blk-blue'),
      },
      {
        label: ODemographicChartLabels.HIS,
        color: getComputedColor('--ba-tertiary'),
      },
      {
        label: ODemographicChartLabels.IND,
        color: getComputedColor('--ba-nat-ind-golden-rod'),
      },
      {
        label: ODemographicChartLabels.PAC,
        color: getComputedColor('--ba-pac-pink'),
      },
      {
        label: ODemographicChartLabels.TWO,
        color: getComputedColor('--ba-two-peach'),
      },
      {
        label: ODemographicChartLabels.WHT,
        color: getComputedColor('--ba-brand-pc-wht-red'),
      },
    ],
    individualRacesAndUnknownChartColors(): IPrepareBarChartColors[] {
      return [...this.individualRacesChartColors, ...this.unknownChartColor]
    },
    individualRacesAndGenderChartColors(): IPrepareBarChartColors[] {
      return [...this.individualRacesChartColors, this.genderChartColors[0]]
    },
    genderAndUnknownChartColors(): IPrepareBarChartColors[] {
      return [...this.genderChartColors, ...this.unknownChartColor]
    },
    minAndNonMinUnknownChartColors(): IPrepareBarChartColors[] {
      return [...this.minAndNonMinChartColors, ...this.unknownChartColor]
    },
    protectedVeteransChartColors: () => [
      {
        label: 'Protected Veteran',
        color: getComputedColor('--ba-pv-hires'),
      },
      {
        label: 'Not Protected Veteran',
        color: getComputedColor('--ba-darkGray'),
      },
    ],
    pvHiresAndApplicantsChartColors: () => [
      {
        label: 'Protected Veteran Hires',
        color: getComputedColor('--ba-pv-hires'),
      },
      {
        label: 'Total Hires',
        color: getComputedColor('--ba-darkGray'),
      },
      {
        label: 'Protected Veteran Applicants',
        color: getComputedColor('--ba-primary'),
      },
      {
        label: 'Total Applicants',
        color: getComputedColor('--ba-tertiary'),
      },
    ],
    iwdEmploymentChartColors: () => [
      {
        label: 'Actual',
        color: getComputedColor('--ba-iwd-hires'),
      },
      {
        label: 'Expected',
        color: getComputedColor('--ba-darkGray'),
      },
    ],
    iwdEmploymentDoughnutChartColors: () => [
      {
        label: 'JGs met',
        color: getComputedColor('--ba-iwd-hires'),
      },
      {
        label: 'JGs not met',
        color: getComputedColor('--ba-darkGray'),
      },
    ],
    iwdHiresDoughnutChartColors: () => [
      {
        label: 'IWD',
        color: getComputedColor('--ba-iwd-hires'),
      },
      {
        label: 'Not IWD',
        color: getComputedColor('--ba-darkGray'),
      },
    ],
    iwdHiresAndApplicantsChartColors: () => [
      {
        label: 'IWD Hires',
        color: getComputedColor('--ba-iwd-hires'),
      },
      {
        label: 'Not IWD Hires',
        color: getComputedColor('--ba-secondaryBluegreen'),
      },
      {
        label: 'IWD Applicants',
        color: getComputedColor('--ba-iwd-applicants'),
      },
      {
        label: 'Not IWD Applicants',
        color: getComputedColor('--ba-darkGray'),
      },
    ],
  },
  actions: {
    async setDefaultMode() {
      try {
        this.currentTheme = ThemeType.DEFAULT
        await this.changeTheme()
      } catch (error) {
        console.error('Error in setDefaultMode:', error)
      }
    },
    async setAccessibilityMode() {
      try {
        this.currentTheme = ThemeType.ACCESSIBILITY_MODE
        await this.changeTheme()
      } catch (error) {
        console.error('Error in setAccessibilityMode:', error)
      }
    },
    async changeTheme() {
      try {
        if (this.currentTheme === ThemeType.DEFAULT) {
          document.documentElement.removeAttribute('data-theme')
        } else {
          document.documentElement.setAttribute('data-theme', this.currentTheme)
        }
      } catch (error) {
        console.error('Error in changeTheme:', error)
      }
    },
  },
})
